import React, { useEffect, useState, useRef, useCallback } from 'react';
import { forceCollide, forceManyBody } from 'd3-force';
import ForceGraph2D from 'react-force-graph-2d';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './GeneNetworkGraph.css';

// --- AESTHETICS: Define our beautiful new color palette ---
const PALETTE = {
  background: '#0a0e1a', // Deeper dark blue
  primary: '#38bdf8',     // Vibrant Sky Blue
  secondary: '#64748b',   // Slate gray for links/text
  glass: {
    gene: 'rgba(56, 189, 248, 0.15)',      // Glass blue for genes
    variant: 'rgba(168, 85, 247, 0.15)',   // Glass purple for variants
    geneBorder: '#38bdf8',
    variantBorder: '#a855f7',
    geneGlow: 'rgba(56, 189, 248, 0.4)',
    variantGlow: 'rgba(168, 85, 247, 0.4)'
  },
  text: {
    gene: '#ffffff',
    variant: '#e2e8f0'
  }
};

const GeneNetworkGraph = ({ data }) => {
  const fgRef = useRef();
  const containerRef = useRef();
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [scoreRange, setScoreRange] = useState([0.75, 1]);
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(1);
  const [parsedData, setParsedData] = useState([]);
  const [totalConnections, setTotalConnections] = useState(0);

  // Detect light mode
  const isLightMode = document.querySelector('.WSNG-app-container')?.classList.contains('light-theme');

  const parseNetworkData = useCallback((rawData) => {
    if (!rawData || typeof rawData !== 'string') return [];
    const lines = rawData.trim().split('\n').slice(1); // Skip header
    const parsed = [];
    for (const line of lines) {
      const values = line.split('\t');
      if (values.length >= 4) {
        const row = {
          from: values[0]?.trim() || '',
          to: values[1]?.trim() || '',
          type: values[2]?.trim() || '',
          weight: parseFloat(values[3]) || 0
        };
        if (row.from && row.to) parsed.push(row);
      }
    }
    return parsed;
  }, []);

  // --- MODIFIED useEffect to handle data changes and reset state ---
  useEffect(() => {
    // If there is no data, reset all relevant states to their initial values.
    if (!data) {
      setParsedData([]);
      setTotalConnections(0);
      setGraphData({ nodes: [], links: [] });
      setMinScore(0);
      setMaxScore(1);
      setScoreRange([0.75, 1]); // Reset to a sensible default
      return;
    }

    const parsed = parseNetworkData(data);
    if (parsed.length > 0) {
      const weights = parsed.map(d => d.weight);
      const min = Math.min(...weights);
      const max = Math.max(...weights);
      
      setMinScore(min);
      setMaxScore(max);
      setTotalConnections(parsed.length);
      
      const defaultMin = 0.75;
      const initialMin = Math.max(min, defaultMin);
      setScoreRange([initialMin > max ? min : initialMin, max]);

      setParsedData(parsed);
    } else {
      // If data is present but parsing results in no connections, still reset.
      setParsedData([]);
      setTotalConnections(0);
      setGraphData({ nodes: [], links: [] });
    }
  }, [data, parseNetworkData]);
  
  // (Dimension handling and other hooks remain the same)
  useEffect(() => {
    const observerTarget = containerRef.current;
    if (!observerTarget || !data) return;
    const setDims = () => {
      const { offsetWidth, offsetHeight } = observerTarget;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setDimensions({ width: offsetWidth, height: offsetHeight });
      }
    };
    const timeoutId = setTimeout(setDims, 150);
    const resizeObserver = new ResizeObserver(entries => {
      if (!entries || !entries.length) return;
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setDimensions({ width, height });
    });
    resizeObserver.observe(observerTarget);
    return () => {
      clearTimeout(timeoutId);
      if (observerTarget) resizeObserver.unobserve(observerTarget);
    };
  }, [data]);


  const processGraphData = useCallback((inputData) => {
    if (!inputData || !inputData.length) {
      return { nodes: [], links: [] }; // Return the new state instead of setting it
    }
    const nodes = [];
    const links = [];
    const nodeMap = {};
    const addNode = (id, type) => {
      if (!nodeMap[id]) {
        const isGene = type === 'gene';
        nodeMap[id] = { id, name: id, isGene, size: isGene ? 22 : 14 };
        nodes.push(nodeMap[id]);
      }
    };
    inputData.forEach(item => {
      addNode(item.from, 'gene');
      addNode(item.to, 'variant');
      links.push({ source: item.from, target: item.to, weight: item.weight });
    });
    return { nodes, links };
  }, []);

  // --- MODIFIED useEffect for processing graph data ---
  useEffect(() => {
    if (!parsedData.length) {
      setGraphData({ nodes: [], links: [] }); // Clear graph data if no parsed data
      return;
    }
    const filtered = parsedData.filter(d => d.weight >= scoreRange[0] && d.weight <= scoreRange[1]);
    const newGraphData = processGraphData(filtered);
    setGraphData(newGraphData);
  }, [parsedData, scoreRange, processGraphData]);

  useEffect(() => {
    const fg = fgRef.current;
    if (fg && graphData.nodes.length > 0) {
      fg.d3Force('charge', forceManyBody().strength(-400));
      fg.d3Force('collide', forceCollide(node => node.size + 10));
      fg.d3Force('link').distance(100);
    }
  }, [graphData.nodes.length]);
  
  useEffect(() => {
    if (fgRef.current && graphData.nodes.length > 0 && dimensions.width > 0) {
      setTimeout(() => fgRef.current?.zoomToFit(400, 100), 300);
    }
  }, [graphData.nodes.length, dimensions]);

  // --- NODE DRAWING: The core of the visual upgrade ---
  const drawNode = useCallback((node, ctx) => {
    const { x, y, size, isGene, name } = node;
    
    // Validate node properties to prevent canvas errors
    if (!isFinite(x) || !isFinite(y) || !isFinite(size) || size <= 0) {
      return; // Skip rendering if values are invalid
    }
    
    // Shared styles
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Enhanced glowing hover effect
    if (node.__hover) {
      const glowColor = isGene ? PALETTE.glass.geneGlow : PALETTE.glass.variantGlow;
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    } else {
      ctx.shadowBlur = 0;
    }

    const radius = isGene ? size * 1.8 : size * 1.4;
    
    // Additional validation for radius
    if (!isFinite(radius) || radius <= 0) {
      return;
    }

    if (isGene) {
      // Glass effect for gene nodes
      const gradient = ctx.createRadialGradient(x, y - radius * 0.8, 0, x, y, radius);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
      gradient.addColorStop(0.3, PALETTE.glass.gene);
      gradient.addColorStop(1, 'rgba(56, 189, 248, 0.05)');
      
      // Main glass circle
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();
      
      // Glass border with glow
      ctx.strokeStyle = PALETTE.glass.geneBorder;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Inner highlight for glass effect
      const highlightGradient = ctx.createRadialGradient(x - radius * 0.3, y - radius * 0.3, 0, x, y, radius * 0.6);
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = highlightGradient;
      ctx.beginPath();
      ctx.arc(x, y, radius * 0.6, 0, 2 * Math.PI);
      ctx.fill();
      
    } else {
      // Glass effect for variant nodes
      const gradient = ctx.createRadialGradient(x, y - radius * 0.8, 0, x, y, radius);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
      gradient.addColorStop(0.9, PALETTE.glass.variant);
      gradient.addColorStop(1, 'rgba(168, 85, 247, 0.05)');
      
      // Main glass circle
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();
      
      // Glass border
      ctx.strokeStyle = PALETTE.glass.variantBorder;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      
      // Inner highlight
      const highlightGradient = ctx.createRadialGradient(x - radius * 0.8, y - radius * 1, 0, x, y, radius * 0.5);
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = highlightGradient;
      ctx.beginPath();
      ctx.arc(x, y, radius * 0.5, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Reset shadow for text
    ctx.shadowBlur = 0;
    
    // Smart text rendering with proper truncation
    if (!name || typeof name !== 'string') {
      return; // Skip text rendering if name is invalid
    }
    
    const maxChars = isGene ? 8 : 6;
    const fontSize = isGene ? size * 0.8 : size * 0.6;
    
    // Validate fontSize
    if (!isFinite(fontSize) || fontSize <= 0) {
      return;
    }
    
    ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    ctx.fillStyle = isLightMode
      ? (isGene ? '#172b4d' : '#5e6c84')  // dark text for light mode
      : (isGene ? PALETTE.text.gene : PALETTE.text.variant);  // white text for dark mode
    
    // Measure text and truncate if necessary
    let displayText = name;
    const maxWidth = radius * 1.6;
    let textWidth = ctx.measureText(displayText).width;
    
    if (isFinite(textWidth) && textWidth > maxWidth) {
      // Truncate and add ellipsis
      while (textWidth > maxWidth && displayText.length > 3) {
        displayText = displayText.slice(0, -1);
        textWidth = ctx.measureText(displayText + '…').width;
      }
      displayText += '…';
    }
    
    // Add text shadow for better readability
    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    ctx.fillText(displayText, x, y);
    
    // Reset shadow
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }, [isLightMode]); // Added isLightMode dependency

  const Placeholder = ({ icon, message }) => (
    <div className="gene-network-placeholder"><div className="placeholder-content"><i className={icon}></i><p>{message}</p></div></div>
  );

  const getPlaceholder = () => {
    if (!data) return <Placeholder icon="fas fa-project-diagram" message="No network data provided." />;
    if (data && !parsedData.length) return <Placeholder icon="fas fa-exclamation-triangle" message="No valid interactions found in data." />;
    if (dimensions.width === 0) return <Placeholder icon="fas fa-ruler-combined" message="Initializing canvas..." />;
    if (graphData.nodes.length === 0) return <Placeholder icon="fas fa-filter" message="No results for this weight range. Try expanding it." />;
    return null;
  }
  
  const placeholder = getPlaceholder();

  return (
    <div className="gene-network-container">
      <div className="gene-network-controls">
        <div className="score-range-control">
          <label>Weight</label>
          <div className="range-slider-container">
            <span className="range-value">{scoreRange[0].toFixed(3)}</span>
            <Slider
              range
              min={minScore}
              max={maxScore}
              step={(maxScore - minScore) / 1000 || 0.01}
              value={scoreRange}
              onChange={setScoreRange}
              allowCross={false}
              className="range-slider-theme"
            />
            <span className="range-value">{scoreRange[1].toFixed(3)}</span>
          </div>
        </div>
        <div className="network-stats">
          <span className="stat-item"><strong>Genes:</strong> {graphData.nodes.filter(n => n.isGene).length}</span>
          <span className="stat-item"><strong>Variants:</strong> {graphData.nodes.filter(n => !n.isGene).length}</span>
          <span className="stat-item">
            <strong>Connections:</strong> {graphData.links.length}
            {totalConnections > 0 && totalConnections !== graphData.links.length && (
              <span className="total-connections"> / {totalConnections}</span>
            )}
          </span>
        </div>
      </div>

      <div ref={containerRef} className="gene-network-graph" >
        {placeholder ? placeholder : (
          <ForceGraph2D
            ref={fgRef}
            width={dimensions.width}
            height={dimensions.height}
            graphData={graphData}
            nodeLabel={node => `${node.isGene ? 'Gene' : 'Variant'}: ${node.name}`}
            nodeCanvasObject={drawNode}
            linkColor={() => PALETTE.secondary + '60'} // semi-transparent
            linkWidth={1}
            cooldownTicks={150}
            onEngineStop={() => fgRef.current?.zoomToFit(400, 100)}
          />
        )}
      </div>
    </div>
  );
};

export default GeneNetworkGraph;